/**
 * Roomgrid layout.
 *
 * Fixed-viewport shell: 222px sidebar (border-right, no gap) + 56px topbar
 * + flat content area on the warm app background (20px 24px padding, own
 * scroll region). Cards/tables inside pages provide their own elevated
 * surfaces — the layout no longer wraps content in a card.
 */

import React, { useEffect } from "react";
import { Layout, LayoutProps, useSidebarState } from "react-admin";
import { useTheme, useMediaQuery } from "@mui/material";
import { CustomMenu } from "./CustomMenu";
import { CustomAppBar } from "./CustomAppBar";
import { TopbarActionsProvider } from "./TopbarActionsContext";
import { GlobalBackdrop } from "@/components/common";

const SIDEBAR_INIT_FLAG = "notifier.sidebar.initialized";
const TOPBAR_HEIGHT = 56;

const useDefaultOpenSidebar = (isMobile: boolean): void => {
  const [, setSidebarOpen] = useSidebarState();
  useEffect(() => {
    if (isMobile) return;
    if (sessionStorage.getItem(SIDEBAR_INIT_FLAG) === "1") return;
    sessionStorage.setItem(SIDEBAR_INIT_FLAG, "1");
    setSidebarOpen(true);
  }, [isMobile, setSidebarOpen]);
};

const SidebarBootstrapper: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  useDefaultOpenSidebar(isMobile);
  return null;
};

export const CustomLayout: React.FC<LayoutProps> = (props) => {
  const theme = useTheme();
  const rg = theme.custom.rg;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <TopbarActionsProvider>
      <Layout
        {...props}
        appBar={CustomAppBar}
        menu={CustomMenu}
        sx={{
          "&": {
            backgroundColor: rg.bg,
          },

          ...(!isMobile && {
            height: "100vh",
            overflow: "hidden",
          }),

          // AppBar on top, then the sidebar+content row below.
          "& .RaLayout-appFrame": {
            marginTop: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
          },

          // Sidebar column: our menu box defines the width; strip
          // react-admin/MUI drawer sizing so there is no extra gutter.
          "& .RaSidebar-root": {
            height: "100%",
            width: "auto",
            minWidth: 0,
            borderRight: "none",
          },
          "& .RaSidebar-fixed": {
            position: "relative",
            height: "100%",
            width: "auto",
          },

          // Main column: topbar + scrollable content.
          "& .RaLayout-contentWithSidebar": {
            display: "flex",
            flexDirection: "row",
            flex: 1,
            minHeight: 0,
            gap: 0,
            width: "100%",
            padding: 0,
            ...(!isMobile && { overflow: "hidden" }),
          },

          // The content region ITSELF is the scroller (full-bleed): the
          // wheel works anywhere in the page, whatever the page root is
          // (div, form…) and however narrow its content column.
          "& .RaLayout-content": {
            flex: 1,
            display: "block",
            minWidth: 0,
            backgroundColor: rg.bg,
            padding: isMobile ? "14px 12px" : "20px 24px",
            overflow: "auto",

            ...(isMobile && {
              minHeight: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
            }),
          },

          // Pages flow naturally inside the scroller — no card wrapper,
          // no per-child scroll traps.
          "& .RaLayout-content > *": {
            width: "100%",
            backgroundColor: "transparent",
            borderRadius: 0,
            boxShadow: "none",
          },
        }}
      >
        {/* Global loading overlay — subscribes to the in-flight HTTP
            counter and auto-shows during ANY API call. */}
        <GlobalBackdrop />
        <SidebarBootstrapper isMobile={isMobile} />
        {props.children}
      </Layout>
    </TopbarActionsProvider>
  );
};

export default CustomLayout;
